import { LayerData } from '@/atoms/layers.atom';
import { highlightedFeatureIdsAtom, highlightedLayerIdAtom } from '@/atoms/selection.atom';
import { ProjectShareDialogContent } from '@/components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Pane } from '@/components/ui/pane';
import { ViewerSettingsDialog } from '@/components/ViewerSettingsDialog';
import { execute } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton, { iAction } from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import Layers from '@/pages/Project/Viewer/Layers';
import { useDialog } from '@/providers/DialogProvider';
import { layersQuery } from '@/queries/layer.query';
import { CogFourSolid, Edit, LayersThree, PanelRight, Share, Upload } from '@mynaui/icons-react';
import { useQuery } from '@tanstack/react-query';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAtom, useSetAtom } from 'jotai';
import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { FeatureMoveActions } from '../Viewer/ViewerPage';
import IconFull from './icons/Full';
import IconFullViewer from './icons/FullViewer';
import IconMapOneByOneHorizontal from './icons/MapOneByOneHorizontal';
import IconMapOneByOneVertical from './icons/MapOneByOneVertical';
import IconMapViewerHorizontal from './icons/MapViewerHorizontal';
import IconMapViewerVertical from './icons/MapViewerVertical';
import IconOneByOneHorizontal from './icons/OneByOneHorizontal';
import IconOneByOneVertical from './icons/OneByOneVertical';
import IconTwoByOneHorizontal from './icons/TwoByOneHorizontal';
import IconTwoByOneVertical from './icons/TwoByOneVertical';
import { Table } from './Table';

const Viewer = lazy(() => import('@/components/Viewer'));
const MapView = lazy(() => import('@/components/MapView'));

// --- Layout types ---

interface LayoutSplit {
  direction: 'horizontal' | 'vertical';
  defaultSizes?: number[];
  children: LayoutNode[];
}

type LayoutNode = 'viewer' | 'table' | 'map' | LayoutSplit;

interface LayoutConfig {
  label: string;
  icon: ReactNode;
  layout: LayoutNode;
}

// --- Layout helpers ---

const DEFAULT_LAYOUT = 'full-viewer';

function layoutContainsMap(node: LayoutNode): boolean {
  if (node === 'map') return true;
  if (typeof node === 'string') return false;
  return node.children.some(layoutContainsMap);
}

function AttributeViewer({ projectId, hideMap }: { projectId: string; hideMap?: boolean }) {
  const { t } = useTranslation('project');

  return (
    <div className='h-full overflow-hidden relative flex-1'>
      <ErrorBoundary
        fallback={
          <div className='flex items-center justify-center flex-1 text-sm text-muted-foreground'>
            {t('attributes.failedLoadViewer')}
          </div>
        }>
        <Suspense fallback={null}>
          <Viewer projectId={projectId} hideLayers hideMap={hideMap}>
            <FeatureMoveActions />
          </Viewer>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

// --- Recursive renderer ---

interface RenderContext {
  tableProps: Record<string, unknown>;
  projectId: string;
  layerId: string;
  layers: LayerData[];
  hideMap: boolean;
}

function renderNode(node: LayoutNode, ctx: RenderContext, key: string): ReactNode {
  if (node === 'table') {
    return <Table {...(ctx.tableProps as any)} />;
  }
  if (node === 'viewer') {
    return <AttributeViewer projectId={ctx.projectId} hideMap={ctx.hideMap} />;
  }
  if (node === 'map') {
    return (
      <Suspense fallback={null}>
        <MapView projectId={ctx.projectId} mode='standalone' />
      </Suspense>
    );
  }

  return (
    <Pane direction={node.direction} defaultSizes={node.defaultSizes} persistenceKey={key}>
      {node.children.map((child, i) => renderNode(child, ctx, `${key}-${i}`))}
    </Pane>
  );
}

// --- Component ---

export const AttributeTable = () => {
  const { layerId: routeLayerId, projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('project');
  const { organization, hasRole } = useOrganizations();
  const { openDialog } = useDialog();
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const activeLayerId = searchParams.get('layerId') ?? routeLayerId!;

  const _handleSelectLayer = (layerId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('layerId', layerId);
    setSearchParams(next, { replace: true });
  };

  const LAYOUTS: Record<string, LayoutConfig> = {
    'full-viewer': {
      label: t('attributes.viewerOnly'),
      icon: <IconFullViewer />,
      layout: 'viewer',
    },
    '1-1-vertical-split': {
      label: t('attributes.viewerTable11'),
      icon: <IconOneByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['viewer', 'table'],
      },
    },
    '1-1-map-vertical-split': {
      label: t('attributes.mapTable11'),
      icon: <IconMapOneByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['map', 'table'],
      },
    },
    '1-1-map-viewer-vertical-split': {
      label: t('attributes.mapViewer11'),
      icon: <IconMapViewerVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: ['map', 'viewer'],
      },
    },
    '2-1-vertical-split': {
      label: t('attributes.mapViewerTable211'),
      icon: <IconTwoByOneVertical />,
      layout: {
        direction: 'vertical',
        defaultSizes: [50, 50],
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'viewer'],
          },
          'table',
        ],
      },
    },
    '1-1-horizontal-split': {
      label: t('attributes.viewerTable11h'),
      icon: <IconOneByOneHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['viewer', 'table'],
          },
        ],
      },
    },
    '1-1-map-horizontal-split': {
      label: t('attributes.mapTable11h'),
      icon: <IconMapOneByOneHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'table'],
          },
        ],
      },
    },
    '1-1-map-viewer-horizontal-split': {
      label: t('attributes.mapViewer11h'),
      icon: <IconMapViewerHorizontal />,
      layout: {
        direction: 'vertical',
        children: [
          {
            direction: 'horizontal',
            children: ['map', 'viewer'],
          },
        ],
      },
    },
    '2-1-horizontal-split': {
      label: t('attributes.mapViewerTable21h'),
      icon: <IconTwoByOneHorizontal />,
      layout: {
        direction: 'horizontal',
        children: [
          {
            direction: 'vertical',
            children: ['viewer', 'map'],
          },
          'table',
        ],
      },
    },
    full: {
      label: t('attributes.fullTable'),
      icon: <IconFull />,
      layout: 'table',
    },
  };

  const LAYOUT_KEYS = Object.keys(LAYOUTS);
  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();
  const [highlightedIds, setHighlightedIds] = useAtom(highlightedFeatureIdsAtom);
  const setHighlightedLayerId = useSetAtom(highlightedLayerIdAtom);

  const selectedFeatures = useMemo(() => Array.from(highlightedIds), [highlightedIds]);

  const _handleSelect = (selection: string[]) => {
    setHighlightedIds(new Set(selection));
    setHighlightedLayerId(activeLayerId ?? null);
  };

  // Selection set is keyed only by feature.id, so highlights from a different
  // layer are meaningless once the user switches layers. Clear on layer change
  // and on unmount so the viewers don't show stale highlights elsewhere.
  useEffect(() => {
    setHighlightedIds(new Set());
    setHighlightedLayerId(activeLayerId ?? null);
  }, [activeLayerId, setHighlightedIds, setHighlightedLayerId]);

  useEffect(() => {
    return () => {
      setHighlightedIds(new Set());
      setHighlightedLayerId(null);
    };
  }, [setHighlightedIds, setHighlightedLayerId]);

  const { data } = useQuery({
    queryKey: ['layers', project?.id],
    queryFn: () => execute(layersQuery, { projectId: project?.id }),
    enabled: !!project?.id,
  });

  const layers = data?.layers ?? [];

  const layoutKey = LAYOUT_KEYS.includes(searchParams.get('layout') ?? '')
    ? searchParams.get('layout')!
    : DEFAULT_LAYOUT;
  const layout = LAYOUTS[layoutKey];

  const setLayout = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === DEFAULT_LAYOUT) {
      next.delete('layout');
    } else {
      next.set('layout', key);
    }
    setSearchParams(next, { replace: true });
  };

  const isLayersEnabled = searchParams.get('layers') === 'true';

  const _handleToggleLayers = () => {
    const next = new URLSearchParams(searchParams);
    next.set('layers', isLayersEnabled ? 'false' : 'true');
    analytics.capture(isLayersEnabled ? 'Project Layers Disabled' : 'Project Layers Enabled');
    setSearchParams(next, { replace: true });
  };

  // --- Header actions ---

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';

  const roleActions: (iAction | null)[] = [];

  if (hasRole('EDITOR')) {
    roleActions.push({
      icon: <Share />,
      onClick: () => {
        analytics.capture('Project Share Opened');
        openDialog(<ProjectShareDialogContent projectId={project?.id!} />, 'sm:max-w-[800px]');
      },
      tooltip: t('viewer.shareProject'),
    });
  }
  if (hasRole('EDITOR')) {
    roleActions.push({
      icon: <Edit />,
      onClick: () => {
        if (!organization) throw new Error('Organization is not defined');
        analytics.capture('Project Edit Opened');
        navigate(`/projects/${project?.id}/edit`);
      },
      tooltip: t('viewer.editProject'),
      shortcutKey: `${mod}+⇧+E`,
    });
    roleActions.push({
      icon: <Upload />,
      onClick: () => {
        if (!organization) throw new Error('Organization is not defined');
        analytics.capture('Project Upload Opened');
        navigate(`/projects/${project?.id}/upload`);
      },
      tooltip: t('viewer.dataCenter'),
      shortcutKey: `${mod}+⇧+U`,
    });
    roleActions.push({
      icon: <LayersThree />,
      onClick: () => {
        analytics.capture('Project Layers Opened');
        navigate(`/projects/${project?.id}/layers`);
      },
      tooltip: t('layers.title'),
    });
    roleActions.push(null);
  }

  usePageMeta({ title: [project?.name] });

  const tableProps = {
    projectId: project?.id!,
    layerId: activeLayerId,
    selection: selectedFeatures,
    onSelect: _handleSelect,
    layers,
  };

  return (
    <>
      <PageSkeleton
        steps={projectLoading ? [{ label: t('edit.loading'), href }] : [{ label: project.name, href }]}
        actions={[
          ...roleActions,

          {
            icon: <CogFourSolid />,
            onClick: () => {
              analytics.capture('Project Settings Opened');
              setSettingsDialogOpen(true);
            },
            tooltip: t('viewer.viewerSettings'),
          },
          {
            element: (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className='size-7 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200/70 rounded-md text-slate-500 hover:text-slate-700 [&>svg]:size-4 [&>svg]:stroke-[2.25px] dark:text-stone-100 dark:hover:text-stone-100 dark:hover:bg-stone-800/50 dark:active:bg-stone-800/50'>
                    {layout.icon}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {LAYOUT_KEYS.map((key) => {
                    const config = LAYOUTS[key];
                    const Icon = config.icon;
                    return (
                      <DropdownMenuItem key={key} onClick={() => setLayout(key)}>
                        {Icon}
                        {config.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
            tooltip: t('attributes.layout'),
          },
          null,
          {
            icon: <PanelRight />,
            onClick: _handleToggleLayers,
            tooltip: t('viewer.toggleLayers'),
            appearance: isLayersEnabled ? 'primary' : 'secondary',
            shortcutKey: `${mod}+⇧+L`,
          },
        ]}>
        <div className='flex flex-1 overflow-hidden'>
          <div className='flex-1 min-w-0 overflow-hidden flex'>
            {projectId
              ? renderNode(
                  layout.layout,
                  {
                    tableProps,
                    projectId,
                    layerId: activeLayerId,
                    layers,
                    hideMap: layoutContainsMap(layout.layout),
                  },
                  `attr-pane-${layoutKey}`,
                )
              : 'Loading...'}
          </div>
          <ErrorBoundary
            fallback={
              <div className='flex flex-col items-center justify-center px-4'>{t('viewer3d.failedLoadLayers')}</div>
            }>
            <Layers projectId={projectId} selectedLayerId={activeLayerId} onSelectLayer={_handleSelectLayer} />
          </ErrorBoundary>
        </div>
      </PageSkeleton>
      {settingsDialogOpen && <ViewerSettingsDialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen} />}
    </>
  );
};
